import { Button, Dialog, DialogActions, DialogContent, DialogContentText, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useMutation } from '@tanstack/react-query';
import * as React from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { useAddressKitInfo } from 'v2/hooks/useAddressKitInfo';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import kitService from 'v2/services/kit';

import type { AddressFormKit } from 'v2/interfaces/addressKit';

import AddressToDeliveryKit from './AddressToDeliveryKit';
import ConfirmAddress from './ConfirmSameAddress';
import { useStyles } from './styles';
import ConfirmAddressToKits from './UpdateAddressForm';
import DialogTitle from '../DialogTitle';

const ModalBelt = () => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOpen, setIsOpen] = React.useState(false);
  const [isOpenChangeAddress, setIsOpenChangeAddress] = React.useState(false);
  const [isOpenConfirmAddress, setIsOpenConfirmAddress] = React.useState(false);
  const { refetch: fetchAddressKitInfo, isLoading, isError, data, remove: invalidate, isRefetching } = useAddressKitInfo();

  const onSaveNewAddressToKit = (data: AddressFormKit) => {
    const formattedData = {
      ...data,
      number: data.number || null,
    };

    return kitService.saveNewAddressToKit(formattedData);
  };

  const saveNewAddressToKit = useMutation({
    mutationFn: onSaveNewAddressToKit,
    onSuccess: () => invalidate(),
  });

  useObservable(() => {
    return combineLatest([eduzzAccountsService.getUser(), eduzzAccountsService.getOriginalUser()]).pipe(
      map(([user, originalUser]) => {
        const isNotAccessControl = user?.id === originalUser?.id;

        if (isNotAccessControl && data === undefined) fetchAddressKitInfo();

        const hasData = !!data && !data.answeredAt && !!user && !!originalUser;
        const isReady = !isLoading && !isError && !isRefetching;

        return hasData && isReady && isNotAccessControl;
      }),
      tap((shouldOpen) => setIsOpen(shouldOpen)),
      distinctUntilChanged()
    );
  }, [data, isLoading, isError, isRefetching]);

  const handleChangeAddress = () => {
    setIsOpen(false);
    setIsOpenChangeAddress(true);
  };

  const handleConfirmAddress = async () => {
    try {
      await saveNewAddressToKit.mutateAsync(data.address);
      setIsOpen(false);
      setIsOpenConfirmAddress(true);
    } catch (error) {
      console.error(error);
      toast.error(error);
    }
  };

  const handleClose = () => {
    if (!saveNewAddressToKit.isLoading) setIsOpen(false);
  };

  if (isLoading || isError || !data) return null;

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} fullScreen={isMobile} maxWidth='md'>
        <DialogTitle onClose={handleClose} disableCloseAction={saveNewAddressToKit.isLoading}>
          <Typography>⭐️ Parabéns pela nova conquista!</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            <div className={classes.container}>
              <Typography variant='subtitle1' color='textPrimary' className={classes.bold}>
                Olá {data.name},
              </Typography>
              <Typography color='textPrimary'>Você alcançou o {data.belt}! 🎉🎁🎊</Typography>
              <Typography color='textPrimary'>Sua jornada no 🏆 Programa Elite está evoluindo e queremos celebrar este marco com você.</Typography>
            </div>
            <Typography color='textPrimary'>
              <span className={classes.bold}>Confirme seu endereço</span> para garantir a entrega do seu reconhecimento Elite.
            </Typography>
            <div className={classes.currentAddressBackground}>
              <Typography color='textPrimary' className={classes.bold}>
                Endereço atual:
              </Typography>
              {!data.address ? (
                <Typography color='textPrimary'>Nenhum endereço cadastrado!</Typography>
              ) : (
                <AddressToDeliveryKit address={data.address} />
              )}
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.buttonActionsContainer}>
          {data.address && (
            <Button onClick={handleConfirmAddress} color='inherit' size='large' disabled={saveNewAddressToKit.isLoading}>
              Meu endereço está correto!
            </Button>
          )}
          <Button variant='contained' onClick={handleChangeAddress} color='primary' size='large' disabled={saveNewAddressToKit.isLoading}>
            {data.address ? 'Atualizar endereço de entrega' : 'Cadastrar endereço de entrega'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmAddressToKits isOpen={isOpenChangeAddress} setIsOpen={setIsOpenChangeAddress} data={data} />
      <ConfirmAddress isOpen={isOpenConfirmAddress} setIsOpen={setIsOpenConfirmAddress} data={data} />
    </>
  );
};

export default ModalBelt;
